import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExclamationCircleIcon, CheckIcon, HeartIcon } from '@heroicons/react/24/outline';
import { DemoConversion } from '../../types';
import RelativeTime from '../RelativeTime';
import { apiService } from '../../services/api';
import CopyIconButton from '../CopyIconButton';
import { TableCard, TableHeader, TableViewport, SortableTh, EmptyRow, thClass, tdClass, SortOrder, usePageSize } from '../table/TableKit';
import { StatTile } from './shared';
import { urlEnum, urlPositiveInt, urlString, useUrlState } from '../../hooks/useUrlState';
import { useSearchDraft } from '../../hooks/useSearchDraft';
import { usePageClamp } from '../../hooks/usePageClamp';

const URL_SPEC = {
  q: urlString(),
  sort: urlEnum(['instance_key', 'result', 'duration_ms', 'ai_enabled', 'is_liked', 'started_at'] as const, 'started_at'),
  order: urlEnum(['asc', 'desc'] as const, 'desc'),
  page: urlPositiveInt(),
};

const ConverterDemoTab: React.FC = () => {
  const [pageSize, setPageSize] = usePageSize('admin-converter-demo');
  const [urlState, setUrlState] = useUrlState(URL_SPEC);
  const [searchQuery, setSearchQuery] = useSearchDraft(urlState.q, (q) => setUrlState({ q, page: 1 }));
  const offset = (urlState.page - 1) * pageSize;
  const onSort = (sort: string, order: SortOrder) => setUrlState({
    sort: sort as typeof urlState.sort,
    order,
    page: 1,
  });

  // Fetch converter demo data (paged, search is server-side)
  const { data, isLoading, isPlaceholderData, error } = useQuery({
    queryKey: ['admin', 'converterDemo', offset, pageSize, urlState.q, urlState.sort, urlState.order],
    queryFn: () => apiService.getConverterDemoAdmin(pageSize, offset, urlState.q || undefined, urlState.sort, urlState.order),
    placeholderData: (previousData) => previousData,
    retry: false,
  });

  const summary = data?.summary;
  const conversionsTotal = data?.conversions_total ?? data?.conversions?.length ?? 0;
  const conversions = data?.conversions ?? [];

  usePageClamp({
    page: urlState.page,
    pageSize,
    total: data?.conversions_total ?? data?.conversions?.length,
    ready: !isLoading && !isPlaceholderData && !!data,
    setPage: (page) => setUrlState({ page }),
  });

  const formatDuration = (durationMs: number | null): string => {
    if (durationMs === null) return '-';
    if (durationMs < 1000) return `${durationMs}ms`;
    return `${(durationMs / 1000).toFixed(1)}s`;
  };

  const isConversionError = (item: DemoConversion): boolean => {
    return item.converted_code === null || item.converted_code === '';
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64 text-red-500">
        Failed to load converter demo data.
      </div>
    );
  }

  return (
    <div>
      {/* Summary tiles, same pattern as the other admin pages */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <StatTile label="Total" value={summary.total_conversions} />
          <StatTile label="Success" value={<span className="text-primary">{summary.successful_count}</span>} />
          <StatTile label="Errors" value={<span className="text-red-500">{summary.error_count}</span>} />
          <StatTile label="AI Used" value={summary.ai_enabled_count} />
          <StatTile label="Liked" value={summary.liked_count} />
          <StatTile label="Instances" value={summary.unique_instances} />
        </div>
      )}

      <TableCard>
        <TableHeader
          title="Conversions"
          search={{ value: searchQuery, onChange: setSearchQuery }}
          pagination={{
            offset,
            limit: pageSize,
            total: conversionsTotal,
            onOffsetChange: (nextOffset) => setUrlState({ page: nextOffset / pageSize + 1 }),
            onLimitChange: (size) => {
              setPageSize(size);
              setUrlState({ page: 1 });
            },
          }}
        />
        <TableViewport>
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <SortableTh
                  label="Instance"
                  sortKey="instance_key"
                  sort={urlState.sort}
                  order={urlState.order}
                  onSort={onSort}
                  firstOrder="asc"
                />
                <th className={thClass}>Source</th>
                <SortableTh label="Result" sortKey="result" sort={urlState.sort} order={urlState.order} onSort={onSort} />
                <SortableTh label="Duration" sortKey="duration_ms" sort={urlState.sort} order={urlState.order} onSort={onSort} />
                <SortableTh label="AI" sortKey="ai_enabled" sort={urlState.sort} order={urlState.order} onSort={onSort} />
                <SortableTh label="Liked" sortKey="is_liked" sort={urlState.sort} order={urlState.order} onSort={onSort} />
                <SortableTh
                  label="Time"
                  sortKey="started_at"
                  sort={urlState.sort}
                  order={urlState.order}
                  onSort={onSort}
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {conversions.length === 0 ? (
                <EmptyRow colSpan={7} text="No conversions found." />
              ) : (
                conversions.map((item, index) => (
                  <tr
                    key={`${item.instance_id}-${item.started_at}-${index}`}
                    className="hover:bg-body-bg transition-colors"
                  >
                    <td className={`${tdClass} font-mono`}>{item.instance_key}</td>
                    <td className={tdClass}>
                      <CopyIconButton text={item.source_code} label="source code" />
                    </td>
                    <td className={tdClass}>
                      {isConversionError(item) ? (
                        <span className="text-red-500" title="Conversion error">
                          <ExclamationCircleIcon className="w-5 h-5" />
                        </span>
                      ) : (
                        <CopyIconButton text={item.converted_code!} label="converted code" />
                      )}
                    </td>
                    <td className={`${tdClass} text-secondary`}>{formatDuration(item.duration_ms)}</td>
                    <td className={tdClass}>
                      {item.ai_enabled ? (
                        <CheckIcon className="w-5 h-5 text-primary" />
                      ) : (
                        <span className="text-secondary">-</span>
                      )}
                    </td>
                    <td className={tdClass}>
                      {item.is_liked ? (
                        <HeartIcon className="w-5 h-5 text-red-500" />
                      ) : (
                        <span className="text-secondary">-</span>
                      )}
                    </td>
                    <td className={`${tdClass} text-secondary`}>
                      <RelativeTime value={item.started_at} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </TableViewport>
      </TableCard>
    </div>
  );
};

export default ConverterDemoTab;
